import React, { RefObject, useEffect, useRef, useState } from "react";
import { Drawer, Box, Typography, Button, Fade } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isIOS, isAndroid, mobile, isMobileWebBrowser } from "src/utils/platformUtils";
// import AddIcon from "@mui/icons-material/Add";
// import EditIcon from "@mui/icons-material/Edit";
import { ReactComponent as EditIcon } from "src/assets/EditIcon.svg";
import RefreshIcon from "@mui/icons-material/Refresh";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import settingsIcon from "../../assets/settings.svg";
import roundownIcon from "../../assets/newRundown.svg";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAnchorBottom } from "src/hooks/useAnchorBottom";
import SlidesGetsarted from "src/components/SlidesGetstarted/SlidesGetsarted";
import { Browser } from "@capacitor/browser";
import { useAuth0 } from "@auth0/auth0-react";
import { trackSignupScreenSeenAt, trackWelcomeDrawerDismissedAt } from "src/utils/analyticsCapFirebase";
import { frontEnvNumber } from "src/services/runtimeFrontConfig";

const ACCENT_COLOR = PureLightThemeCaaS.colors.alpha.white[100];
const DRAWER_BG = PureLightThemeCaaS.colors.alpha.black[100];
const DRAWER_TEXT = PureLightThemeCaaS.colors.alpha.white[100];
const DRAWER_TEXT_MUTED = PureLightThemeCaaS.colors.alpha.white[70];

interface WelcomeDrawerProps {
  open: boolean;
  onDismiss: () => void;
  leagueSelectorBoxRef?: RefObject<HTMLDivElement | null>;
}

interface WelcomeItem {
  id: string;
  icon: React.ReactNode;
  text: React.ReactNode;
}

// const ConnectLeagueFabIcon = () => (
//   <Box
//     sx={{
//       width: 26,
//       height: 26,
//       minWidth: 26,
//       minHeight: 26,
//       borderRadius: "50%",
//       bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//       flexShrink: 0,
//     }}
//   >
//     <AddIcon sx={{ fontSize: 20, color: "#fff" }} />
//   </Box>
// );

const ConnectLeagueSyncButtonPreview = () => (
  <Button
    variant="contained"
    disableRipple
    tabIndex={-1}
    sx={{
      width: "50px",
      minHeight: "30px",
      py: 0.5,
      px: 0,
      bgcolor: "#FF672D",
      color: "#ffffff",
      fontWeight: 700,
      fontSize: "9px",
      textTransform: "none",
      borderRadius: "8px",
      boxShadow: "none",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      "&:hover": {
        bgcolor: "#FF672D",
        boxShadow: "none",
      },
    }}
  >
    Sync League
  </Button>
);

const WelcomeIconCircle = ({ children, inline = false }: { children: React.ReactNode; inline?: boolean }) => (
  <Box
    component={"div"}
    sx={{
      width: 26,
      height: 26,
      minWidth: 26,
      minHeight: 26,
      borderRadius: "50%",
      bgcolor: PureLightThemeCaaS.colors.alpha.black[100],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      verticalAlign: undefined,
    }}
  >
    {children}
  </Box>
);

// Tint monochrome SVG assets via CSS mask + bgcolor
const AccentSvgIcon = ({ src, size = 26 }: { src: string; size?: number }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      bgcolor: ACCENT_COLOR,
      mask: `url(${src}) center / contain no-repeat`,
      WebkitMask: `url(${src}) center / contain no-repeat`,
    }}
  />
);

const WELCOME_ITEMS: WelcomeItem[] = [
  {
    id: "connect-league",
    icon: <ConnectLeagueSyncButtonPreview />,
    text: "Connect a fantasy platform for a league-tailored experience",
  },
  {
    id: "edit-league",
    icon: (
      <WelcomeIconCircle>
        <EditIcon width={16} height={16} color={PureLightThemeCaaS.colors.alpha.white[100]} />
      </WelcomeIconCircle>
    ),
    text: "Modify or supplement your league settings for the most accurate context",
  },
  {
    id: "refresh-league",
    icon: (
      <WelcomeIconCircle>
        <RefreshIcon sx={{ fontSize: 20, color: ACCENT_COLOR }} />
      </WelcomeIconCircle>
    ),
    text: "Manually refresh your league data when needed",
  },
  {
    id: "mute-quan",
    icon: (
      <WelcomeIconCircle>
        <VolumeOffIcon sx={{ fontSize: 20, color: ACCENT_COLOR }} />
      </WelcomeIconCircle>
    ),
    text: "Toggle the speaker icon to mute and unmute Quan's voice (mute reduces processing time)",
  },
  {
    id: "disable-quan",
    icon: (
      <WelcomeIconCircle>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
      </WelcomeIconCircle>
    ),
    text: (
      <>
        Found within Settings (
        {/* <WelcomeIconCircle inline> */}
        <Box
          component="span"
          sx={{
            width: 22,
            height: 22,
            minWidth: 22,
            minHeight: 22,
            borderRadius: "50%",
            bgcolor: PureLightThemeCaaS.colors.alpha.black[100],
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            verticalAlign: "middle",
          }}
        >
          <AccentSvgIcon src={settingsIcon} size={18} />
        </Box>
        {/* </WelcomeIconCircle> */}
        ), toggle the AI icon for a text - only experience
      </>
    ),
  },
  // {
  //   id: "load-analysis",
  //   icon: <AccentSvgIcon src={roundownIcon} />,
  //   text: "Load team analysis, waivers, draft or sit/start before using the chatbot for an even more informed chatbot experience.",
  // },

];

type WelcomeDrawerStep = "slides" | "welcome";

const WelcomeDrawer: React.FC<WelcomeDrawerProps> = ({ open, onDismiss, leagueSelectorBoxRef }) => {
  const leagueSelectorBottom = useAnchorBottom(leagueSelectorBoxRef, open);
  const drawerRef = useRef<HTMLDivElement>(null);
  // SlidesGetsarted fills this so outside-click skip uses the current slide (same as X)
  const skipSlidesRef = useRef<(() => void) | null>(null);
  const dismissTrackedRef = useRef(false);
  // Two-step onboarding: story slides first, then tips; Let's Begin (or outside/X) dismisses
  const [step, setStep] = useState<WelcomeDrawerStep>("welcome"); // SI PONEMOS DE NUEVO SLIDES COMO DEFUALT, PONERLO AQUI

  const isMobile = mobile || isMobileWebBrowser();
  const { loginWithRedirect } = useAuth0();
  const loggedFreeCreditsOneTime = frontEnvNumber("REACT_APP_LOGGED_FREE_CREDITS_ONE_TIME", 50);

  // Same Auth0 signup flow as FooterSignUp "Sign Up For 50 Free Credits"
  const handleSignUpForFree = async (event: React.MouseEvent) => {
    event.stopPropagation();
    await trackSignupScreenSeenAt("locked_feature", "welcome_drawer");
    await loginWithRedirect({
      async openUrl(url) {
        await Browser.open({
          url,
          windowName: "_self",
        });
      },
    });
  };

  const dismissWelcome = (action: "explore_as_guest" | "outside_click") => {
    if (!dismissTrackedRef.current) {
      dismissTrackedRef.current = true;
      void trackWelcomeDrawerDismissedAt(action);
    }
    onDismiss();
  };

  // Reset to slides when opening (not on close — avoid flashing slides during close animation)
  useEffect(() => {
    if (open) {
      dismissTrackedRef.current = false;
      setStep("welcome"); // SI PONEMOS DE NUEVO SLIDES COMO DEFUALT, PONERLO AQUI
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (e: PointerEvent) => {
      // If the click was inside the drawer, do nothing
      if (drawerRef.current && drawerRef.current.contains(e.target as Node)) {
        return;
      }
      // If the click was outside the drawer, close the drawer
      if (step === "slides" && skipSlidesRef.current) {
        skipSlidesRef.current();
        return;
      }
      dismissWelcome("outside_click");
    };

    // pointerdown in capture runs before AudioSound's preventDefault blocks mousedown
    document.addEventListener("pointerdown", handleOutsideClick, true);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick, true);
    };
  }, [open, onDismiss, step]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => {
        if (step === "slides" && skipSlidesRef.current) {
          skipSlidesRef.current();
          return;
        }
        dismissWelcome("outside_click");
      }}
      hideBackdrop={true}
      sx={{ pointerEvents: "none" }}
      ModalProps={{
        keepMounted: true,
        sx: {
          pointerEvents: "none",
          // Slides step: same full-height offset as HelpMenu; welcome tips stay anchored under league selector
          ...(step === "slides" && {
            top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
          }),
          zIndex: 1440,
        },
      }}
      PaperProps={{
        ref: drawerRef,
        sx: {
          pointerEvents: "auto",
          width: { xs: "100%", sm: "720px", md: "720px", lg: "720px" },
          margin: { xs: "0", sm: "0 auto", md: "0 auto", lg: "0 auto" },
          ...(step === "slides"
            ? {
              // Slides step: always full height (ignore quan) so drawer opens to the top
              height: isIOS || isAndroid
                ? "calc(100svh - env(safe-area-inset-top))"
                : "100%",
              maxHeight: "none",
            }
            : leagueSelectorBottom != null && {
              top: `${leagueSelectorBottom}px`,
              bottom: 0,
              height: "auto",
              maxHeight: "none",
            }),
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: DRAWER_BG,
          zIndex: 1440,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: step === "slides" ? "100%" : "auto",
            p: step === "slides" ? 0 : 1,
            pt: step === "slides" ? 0 : 0.5,
            pb: step === "welcome" ? (isIOS || isAndroid ? "max(env(safe-area-inset-bottom), 10px)" : 1) : 0,
            bgcolor: DRAWER_BG,
          }}
        >
          {/*  SI PONEMOS DE NUEVO SLIDES COMO DEFUALT, descomentar las siguientes lineas (y el final del render condicional obivo) */}
          {/* {step === "slides" ? ( 
            <SlidesGetsarted onNext={() => setStep("welcome")} onClose={onDismiss} skipCloseRef={skipSlidesRef} />
          ) : ( */}
          <>
            <Typography
              variant="h4"
              sx={{
                color: DRAWER_TEXT,
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "center",
                lineHeight: 1.2,
                py: 1.5,
                px: 1,
              }}
            >
              Welcome to RotoGPT!

            </Typography>
            {/* <Typography
                variant="h4"
                sx={{
                  color: DRAWER_TEXT,
                  fontSize: "15px",
                  fontWeight: "bold",
                  textAlign: "center",
                  lineHeight: 1.2,
                  mb: 0.5,
                  px: 1,
                }}
              >
                We help you make better fantasy football decisions faster.
                {/* <Box component="span" sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100] }}>
                  We
                </Box>{" "} */}
            {/* help you make better fantasy football decisions faster. */}
            {/* </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: DRAWER_TEXT_MUTED,
                  fontSize: "13px",
                  fontWeight: "bold",
                  textAlign: "center",
                  pb: 0.5,
                }}
              >
                Tips to make the most of our platform.
              </Typography> */}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
              {WELCOME_ITEMS.map((item) => (
                <Box
                  key={item.id}
                  sx={{ display: "flex", alignItems: "center", gap: item.id === "connect-league" ? 0.8 : 2.5, px: item.id === "connect-league" ? 0 : 2 }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      mt: 0.25,
                      width: item.id === "connect-league" ? "auto" : 32,
                      display: "flex",
                      justifyContent: item.id === "connect-league" ? "flex-start" : "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: DRAWER_TEXT,
                      fontSize: "14px",
                      lineHeight: 1.2,
                      flex: 1,
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>



            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "stretch",
                gap: 1,
                px: 2.5,
              }}
            >
              <Button
                variant="contained"
                onClick={() => dismissWelcome("explore_as_guest")}
                sx={{
                  flex: 1,
                  height: isMobile ? "40px" : "40px",
                  py: 0.5,
                  px: 0.5,
                  mt: 1,
                  borderRadius: 1.5,
                  fontWeight: "bold",
                  fontSize: isMobile ? "13px" : "15px",
                  lineHeight: 1.4,
                  textTransform: "none",
                  color: "#fff",
                  background: PureLightThemeCaaS.colors.alpha.gray[70],
                  border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                  boxShadow: "none",
                  "&:hover": {
                    background: PureLightThemeCaaS.colors.alpha.gray[70],
                    boxShadow: "none",
                  },
                }}
              >
                Explore as a Guest
              </Button>

              <Button
                variant="contained"
                onClick={handleSignUpForFree}
                sx={{
                  flex: 1,
                  height: isMobile ? "40px" : "40px",
                  py: 0.5,
                  px: 0.5,
                  mt: 1,
                  borderRadius: 1.5,
                  fontWeight: "bold",
                  fontSize: isMobile ? "13px" : "15px",
                  lineHeight: 1.15,
                  textTransform: "none",
                  color: "#fff",
                  background: PureLightThemeCaaS.colors.info[100],
                  boxShadow: "none",
                  "&:hover": {
                    background: PureLightThemeCaaS.colors.info[100],
                    boxShadow: "none",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1.15,
                  }}
                >
                  <Box component="span">Create Account</Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "bold",
                      lineHeight: 1.15,
                    }}
                  >
                    {`(Free ${loggedFreeCreditsOneTime} Credits)`}
                  </Box>
                </Box>
              </Button>
            </Box>
          </>
          {/* )} */}
        </Box>
      </Fade>
    </Drawer>
  );
};

export default WelcomeDrawer;
